
import { MLCourse } from './types';

export const ML_INTERMEDIATE_COURSE: MLCourse = {
  id: 'ml-intermediate',
  title: 'Intermediate Machine Learning',
  description: 'Advance your ML skills with ensemble methods, deep learning, NLP, and computer vision. Build production-ready ML pipelines.',
  level: 'intermediate',
  totalDuration: '15-18 hours',
  image: '/api/placeholder/400/300',
  prerequisites: [
    'Completed ML Fundamentals course',
    'Comfortable with Python programming',
    'Understanding of basic statistics',
    'Experience with Pandas and NumPy'
  ],
  learningOutcomes: [
    'Master ensemble methods and advanced algorithms',
    'Build neural networks and deep learning models',
    'Process and analyze text data with NLP',
    'Create computer vision applications',
    'Optimize model performance through hyperparameter tuning',
    'Develop end-to-end ML pipelines',
    'Handle time series data and forecasting',
    'Deploy models for real-world applications'
  ],
  modules: [
    {
      id: 'ml-advanced-supervised',
      title: 'Advanced Supervised Learning & Ensemble Methods',
      description: 'Master Random Forests, Support Vector Machines, and ensemble techniques for superior model performance.',
      duration: '2.5 hours',
      difficulty: 'intermediate',
      objectives: [
        'Understand ensemble learning principles',
        'Implement Random Forests and Gradient Boosting',
        'Apply Support Vector Machines effectively',
        'Combine multiple models for better predictions'
      ],
      content: `# Advanced Supervised Learning & Ensemble Methods

Ensemble methods combine multiple models to create stronger predictions than any individual model.

## Random Forest

### Concept:
- Combines many decision trees
- Each tree uses random subset of features
- Final prediction is average (regression) or vote (classification)

### Advantages:
- Reduces overfitting compared to single trees
- Handles missing values well
- Provides feature importance scores
- Works well out-of-the-box

### Key Parameters:
- **n_estimators**: Number of trees
- **max_depth**: Maximum tree depth
- **max_features**: Features per tree
- **min_samples_split**: Minimum samples to split

## Support Vector Machines (SVM)

### Concept:
- Finds optimal boundary between classes
- Maximizes margin between classes
- Can handle non-linear patterns with kernels

### Kernels:
- **Linear**: For linearly separable data
- **RBF (Radial Basis Function)**: Most common, handles non-linear patterns
- **Polynomial**: For polynomial relationships
- **Sigmoid**: Neural network-like

### When to Use SVM:
- High-dimensional data
- Clear margin between classes
- Memory efficient for large datasets

## Gradient Boosting

### Concept:
- Builds models sequentially
- Each new model corrects previous model's errors
- Combines weak learners into strong learner

### Popular Implementations:
- **XGBoost**: Fast, efficient, handles missing values
- **LightGBM**: Memory efficient, fast training
- **CatBoost**: Handles categorical features well

## Ensemble Strategies

### Bagging:
- Train models on different subsets
- Combine predictions by averaging/voting
- Reduces variance (overfitting)

### Boosting:
- Train models sequentially
- Focus on difficult examples
- Reduces bias (underfitting)

### Stacking:
- Use meta-model to combine base models
- More complex but potentially better performance`,
      codeExamples: [
        {
          title: 'Advanced Ensemble Methods Comparison',
          language: 'python',
          code: `from sklearn.ensemble import RandomForestClassifier, GradientBoostingClassifier, VotingClassifier
from sklearn.svm import SVC
from sklearn.model_selection import cross_val_score
from sklearn.datasets import make_classification
from sklearn.preprocessing import StandardScaler
import numpy as np

# Create complex dataset
X, y = make_classification(n_samples=1000, n_features=20, n_informative=10, 
                          n_redundant=5, n_classes=2, random_state=42)

# Scale features for SVM
scaler = StandardScaler()
X_scaled = scaler.fit_transform(X)

# Define models
rf = RandomForestClassifier(n_estimators=100, random_state=42)
svm = SVC(probability=True, random_state=42)
gb = GradientBoostingClassifier(n_estimators=100, random_state=42)

# Individual model performance
models = [('Random Forest', rf, X), ('SVM', svm, X_scaled), ('Gradient Boosting', gb, X)]

for name, model, data in models:
    scores = cross_val_score(model, data, y, cv=5)
    print(f"{name}: {scores.mean():.4f} (+/- {scores.std() * 2:.4f})")

# Ensemble using voting
voting_clf = VotingClassifier(
    estimators=[('rf', rf), ('svm', svm), ('gb', gb)],
    voting='soft'  # Use probability predictions
)

# Note: For voting classifier, we need to fit on scaled data
voting_scores = cross_val_score(voting_clf, X_scaled, y, cv=5)
print(f"Voting Ensemble: {voting_scores.mean():.4f} (+/- {voting_scores.std() * 2:.4f})")

# Feature importance from Random Forest
rf.fit(X, y)
feature_importance = rf.feature_importances_
print(f"Top 5 important features: {np.argsort(feature_importance)[-5:]}")`,
          explanation: 'This example compares advanced ML algorithms and shows how ensemble methods can improve performance by combining multiple models.'
        }
      ],
      practice: {
        task: 'Build an ensemble model that combines Random Forest, SVM, and Gradient Boosting to predict customer churn.',
        starter: `from sklearn.ensemble import RandomForestClassifier, GradientBoostingClassifier, VotingClassifier
from sklearn.svm import SVC
from sklearn.model_selection import train_test_split
from sklearn.preprocessing import StandardScaler
from sklearn.metrics import classification_report
import pandas as pd

# Load customer churn dataset (simulated)
# Features: tenure, monthly_charges, total_charges, contract_type, etc.
# Target: churn (1 = customer left, 0 = customer stayed)

# TODO:
# 1. Preprocess the data appropriately
# 2. Create individual models (Random Forest, SVM, Gradient Boosting)
# 3. Combine them using VotingClassifier
# 4. Compare individual vs ensemble performance
# 5. Analyze feature importance

# Your code here...`,
        hints: [
          'Remember to scale features for SVM',
          'Use soft voting for probability-based predictions',
          'Compare cross-validation scores',
          'Random Forest provides feature importance naturally'
        ]
      }
    },
    {
      id: 'ml-deep-learning-intro',
      title: 'Introduction to Deep Learning',
      description: 'Enter the world of neural networks and deep learning with hands-on TensorFlow and PyTorch examples.',
      duration: '3 hours',
      difficulty: 'intermediate',
      objectives: [
        'Understand neural network architecture',
        'Implement feedforward networks',
        'Master backpropagation and gradient descent',
        'Use TensorFlow/Keras for deep learning'
      ],
      content: `# Introduction to Deep Learning

Deep learning uses neural networks with multiple layers to learn complex patterns in data.

## Neural Network Fundamentals

### Perceptron:
- Single neuron model
- Takes weighted inputs
- Applies activation function
- Produces output

### Multi-Layer Perceptron (MLP):
- Input layer: Receives data
- Hidden layers: Process information
- Output layer: Makes predictions

### Key Components:
1. **Weights**: Connection strengths between neurons
2. **Biases**: Threshold adjustments
3. **Activation Functions**: Non-linear transformations
4. **Loss Function**: Measures prediction errors

## Activation Functions

### Common Types:
- **ReLU**: max(0, x) - Most popular, fast computation
- **Sigmoid**: 1/(1+e^-x) - Output between 0 and 1
- **Tanh**: (e^x - e^-x)/(e^x + e^-x) - Output between -1 and 1
- **Softmax**: For multi-class classification

### Why Non-linearity Matters:
- Linear functions can only learn linear patterns
- Non-linear activations enable complex pattern recognition

## Backpropagation

### Process:
1. Forward pass: Input → Hidden → Output
2. Calculate loss: Compare prediction to actual
3. Backward pass: Propagate errors back through network
4. Update weights: Adjust to reduce errors

### Gradient Descent:
- **Learning Rate**: Step size for weight updates
- **Batch Size**: Number of samples per update
- **Epochs**: Complete passes through training data

## Deep Learning Frameworks

### TensorFlow/Keras:
- High-level API
- Easy model building
- Excellent for beginners

### PyTorch:
- Dynamic computation graphs
- Pythonic interface
- Popular in research

## Common Architectures

### Feedforward Networks:
- Information flows forward only
- Good for tabular data
- Simple to understand and implement

### Convolutional Neural Networks (CNNs):
- Designed for image data
- Use convolution operations
- Share parameters across spatial dimensions

### Recurrent Neural Networks (RNNs):
- Handle sequential data
- Have memory of previous inputs
- Good for time series and text`,
      codeExamples: [
        {
          title: 'Building Neural Networks with TensorFlow/Keras',
          language: 'python',
          code: `import tensorflow as tf
from tensorflow import keras
from sklearn.datasets import make_classification
from sklearn.model_selection import train_test_split
from sklearn.preprocessing import StandardScaler
import numpy as np

# Create dataset
X, y = make_classification(n_samples=1000, n_features=20, n_classes=2, random_state=42)
X_train, X_test, y_train, y_test = train_test_split(X, y, test_size=0.2, random_state=42)

# Scale features
scaler = StandardScaler()
X_train_scaled = scaler.fit_transform(X_train)
X_test_scaled = scaler.transform(X_test)

# Build neural network
model = keras.Sequential([
    keras.layers.Dense(64, activation='relu', input_shape=(20,)),
    keras.layers.Dropout(0.3),  # Prevent overfitting
    keras.layers.Dense(32, activation='relu'),
    keras.layers.Dropout(0.3),
    keras.layers.Dense(1, activation='sigmoid')  # Binary classification
])

# Compile model
model.compile(
    optimizer='adam',
    loss='binary_crossentropy',
    metrics=['accuracy']
)

# Train model
history = model.fit(
    X_train_scaled, y_train,
    epochs=50,
    batch_size=32,
    validation_split=0.2,
    verbose=1
)

# Evaluate model
test_loss, test_accuracy = model.evaluate(X_test_scaled, y_test, verbose=0)
print(f"Test Accuracy: {test_accuracy:.4f}")

# Make predictions
predictions = model.predict(X_test_scaled)
binary_predictions = (predictions > 0.5).astype(int)

# Model summary
model.summary()`,
          explanation: 'This example shows how to build, train, and evaluate a neural network using TensorFlow/Keras for binary classification.'
        }
      ]
    }
    // Additional modules would be added here
  ]
};
